import { useEffect, useState, useMemo } from 'react'
import { withTranslation /* , i18n */ } from 'server/i18n'
import PropTypes from 'prop-types'
import { NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { Flex, Box } from 'reflexbox'
import useFetch from '@hmn/use-fetch'
import getConfig from 'next/config'
import { useRouter } from 'next/router'

import { getImageURL, getBoatApiURL, getBoatLink } from 'helpers/middleware'
import { useBoat, useBoatList } from 'helpers/hooks'

import BoatStickyMenu from 'components/BoatStickyMenu'
import PromoBlock from 'components/PromoBlock'
import BoatVideo from 'components/BoatVideo'
import HelpBlock from 'components/HelpBlock'
import BoatDescription from 'components/BoatDescription'
import BoatSpecifications from 'components/BoatSpecifications'
import PageTitle from 'components/PageTitle'
import BoatInfoHeader from 'components/BoatInfoHeader'
import BoatMainImage from 'components/BoatMainImage'
import BoatStatement from 'components/BoatStatement'
import BlockTitle from 'components/BlockTitle'
import Loader from 'components/Loader'
import Matterport from 'components/Matterport'

import layoutTheme from 'theme/layout-theme'

import Error from '../../_error'

const { publicRuntimeConfig } = getConfig()
const { CLOUDINARY_URL, DOMAIN_ROOT } = publicRuntimeConfig

const PhotoGallerySlider = dynamic(
    () => import(/* webpackChunkName: 'PhotoGallerySlider' */ 'components/PhotoGallerySlider'),
    {
        ssr: false
    }
)

const Boat = ({ t, i18n, error, slug, canonical, boatUrl, ssrBoatData, matterportLink }) => {
    const [randomPromo, setRandomPromo] = useState(1)
    const router = useRouter()

    // Fetch boat data
    const { data: boatData, isLoading: boatDataPending, hasError: boatDataError } = useBoat(slug, ssrBoatData)

    // Fetch boats listing data
    const { data: boatsListing } = useBoatList()

    useEffect(() => {
        // Random promo image
        if (!boatData) {
            return
        }
        setRandomPromo(Math.floor(Math.random() * 3) + 1)
    }, [boatData])

    const priceRanges = [
        {
            id: 1,
            min: 0,
            max: 100000,
            name: '< 100.000 €'
        },
        {
            id: 2,
            min: 100000,
            max: 500000,
            name: '100.000 - 500.000 €'
        },
        {
            id: 3,
            min: 500000,
            max: 1000000,
            name: '500.000 - 1.000.000 €'
        },
        {
            id: 4,
            min: 1000000,
            max: 5000000,
            name: '1.000.000 - 5.000.000 €'
        },
        {
            id: 5,
            min: 5000000,
            max: 10000000,
            name: '5.000.000 - 10.000.000 €'
        },
        {
            id: 6,
            min: 10000000,
            max: 15000000,
            name: '10.000.000 - 15.000.000 €'
        },
        {
            id: 7,
            min: 15000000,
            max: 30000000,
            name: '15.000.000 - 30.000.000 €'
        },
        {
            id: 8,
            min: 30000000,
            max: Number.POSITIVE_INFINITY,
            name: '> 30.000.000 €'
        }
    ]

    const priceRange = price => {
        let rightRange
        priceRanges.forEach(range => {
            if (price >= range.min && price < range.max) {
                rightRange = range
            }
        })
        return rightRange
    }

    const fitsInRange = (price, range) => price >= range.min && price < range.max

    // Filter boats listing data for slider
    const featuredYachts = useMemo(() => {
        const boatType = boatData?.info?.type
        const boatPriceRange = priceRange(boatData.price.value)

        let featured =
            boatsListing && boatsListing.length > 0
                ? boatsListing
                      .filter(boat => boat.slug !== slug)
                      .filter(boat => boat.info.type === boatType)
                      .filter(boat => fitsInRange(+boat.price.value, boatPriceRange))
                      .filter(boat => boat.published)
                      // eslint-disable-next-line no-shadow
                      .map(({ attachments, info, slug, price }) => ({
                          url: getImageURL((attachments || {}).images, {
                              position: 'listing',
                              size: 'featured',
                              hasFallback: true
                          }),
                          name: info?.model || info?.name,
                          condition: t(`controls:${info.condition}`),
                          year: info.year,
                          length: info.length,
                          price: price.displayValue === '0' ? t('price-upon-request') : price.displayValue,
                          vat: price.vat,
                          boatURL: getBoatLink(info.type, slug, i18n.language).href,
                          boatURLas: getBoatLink(info.type, slug, i18n.language).as,
                          currency: price.displayValue === '0' ? '' : price.currency
                      }))
                : []

        // if there is no boats in this price range, show all of same type
        if (featured.length < 2) {
            featured =
                boatsListing && boatsListing.length > 0
                    ? boatsListing
                          .filter(boat => boat.slug !== slug)
                          .filter(boat => boat.info.type === boatType)
                          .filter(boat => boat.published)
                          // eslint-disable-next-line no-shadow
                          .map(({ attachments, info, slug, price }) => ({
                              url: getImageURL((attachments || {}).images, {
                                  position: 'listing',
                                  size: 'featured',
                                  hasFallback: true
                              }),
                              name: info?.model || info?.name,
                              condition: t(`controls:${info.condition}`),
                              year: info.year,
                              length: info.length,
                              price: price.displayValue === '0' ? t('price-upon-request') : price.displayValue,
                              vat: price.vat,
                              boatURL: getBoatLink(info.type, slug, i18n.language).href,
                              boatURLas: getBoatLink(info.type, slug, i18n.language).as,
                              currency: price.displayValue === '0' ? '' : price.currency
                          }))
                    : []
        }

        return featured
    }, [t, boatsListing])

    // Boat SEO
    const boatSeoData = useMemo(() => {
        const descriptionMaxLength = 170
        const description = ((((boatData || {}).highlights || {}).about || {}).en || '')
            .split(' ')
            .reduce((all, _, index, chunks) => {
                const desc = chunks.slice(0, index + 1).join(' ')
                if (index < chunks.length - 1) {
                    if (desc.length <= descriptionMaxLength - 3) {
                        return `${desc}...`
                    }
                } else if (desc.length <= descriptionMaxLength) {
                    return desc
                }

                return all
            }, '')
        const { info = {}, highlights = {}, attachments = {} } = boatData || {}
        return {
            title: `${info.model} (${info.brand}) | Merk & Merk`,
            description,
            canonical: `https://merkandmerk.${i18n.language === 'en' ? 'com' : 'de'}${
                getBoatLink(info.type, slug, i18n.language === 'en' ? 'com' : 'de').as
            }`,
            openGraph: {
                title: `${info.model} (${info.brand}) | Merk & Merk`,
                description: (highlights.about || {}).en,
                url: `${DOMAIN_ROOT}${boatUrl || getBoatLink(info.type, slug, i18n.language, true).as}`,
                images: [
                    {
                        url: getImageURL(attachments.images, {
                            position: 'listing',
                            size: 'featured',
                            hasFallback: true
                        })
                    }
                ],
                type: 'website'
            },
            languageAlternates: [
                {
                    hrefLang: 'en',
                    href: `${DOMAIN_ROOT}/buy/${router.query.type}/${router.query.slug}`
                },
                {
                    hrefLang: 'de',
                    href: `${DOMAIN_ROOT}/kaufen/${router.query.type}/${router.query.slug}`
                }
            ]
        }
    }, [boatData])

    const boatAttachmentVideo = useMemo(() => {
        if (!boatData || !boatData.attachments || !boatData.attachments.videos || !boatData.attachments.videos.length) {
            return null
        }

        const media = boatData.attachments.videos.find(video => !!video.url)

        return media
    }, [boatData])

    const boatDescriptionsVideo = useMemo(() => {
        const { language } = i18n
        const texts = boatData?.highlights?.video
        if (!texts) {
            return null
        }
        return ['first', 'second', 'third']
            .map(key => ({
                text: texts[key]?.[language || 'en']
            }))
            .filter(item => item.text)
    }, [boatData])

    // Show something before we get any data (push footer down)
    if (boatDataPending) {
        return <Loader height="100vh" />
    }

    if (error) {
        return <Error statusCode={404} />
    }

    // If error show message and console log the error
    if (boatDataError) {
        /* eslint-disable-next-line no-console */
        return <Loader height="100vh" error="We are sorry, something went wrong. Please try again later." />
    }

    const { language } = i18n
    const { info, engine, attachments, highlights, price, leads } = boatData || {}

    const handleLeads = leadsvar => {
        const leadsNewObject = {}

        if (leads && leads.boat) {
            leadsNewObject.boat = language === 'en' ? leadsvar.boat.en : leadsvar.boat.de
        } else {
            leadsNewObject.boat = ''
        }

        if (leads && leads.exterior) {
            leadsNewObject.exterior = language === 'en' ? leadsvar.exterior.en : leadsvar.exterior.de
        } else {
            leadsNewObject.exterior = ''
        }

        if (leads && leads.interior) {
            leadsNewObject.interior = language === 'en' ? leadsvar.interior.en : leadsvar.interior.de
        } else {
            leadsNewObject.interior = ''
        }

        return leadsNewObject
    }

    let interiorImages
    let exteriorImages

    if (boatData && attachments && highlights) {
        interiorImages = getImageURL(attachments.images, {
            position: 'interior',
            size: 'interior-exterior',
            returnList: true
        })

        exteriorImages = getImageURL(attachments.images, {
            position: 'exterior',
            size: 'interior-exterior',
            returnList: true
        })
    }

    return (
        <div>
            <NextSeo {...boatSeoData} />
            {boatData && info && price && (
                <BoatStickyMenu
                    boatUrl={`${DOMAIN_ROOT}${boatUrl ||
                        getBoatLink(((boatData || {}).info || {}).type, slug, i18n.language, true).as}`}
                    boatTitle={info.model}
                    boatLengthOverAll={`${info.length}`}
                    boatYear={info.year}
                    boatPrice={
                        +price.displayValue === 0 ? t('price-upon-request') : `${price.currency} ${price.displayValue}`
                    }
                    boatID={boatData.id}
                />
            )}
            <Flex
                theme={layoutTheme}
                justifyContent="space-between"
                alignItems="flex-end"
                maxWidth={1350}
                mx="auto"
                px={['20px', null, null, null, null, 0]}
                mt={['80px', null, null, '140px']}
                mb={['30px', null, null, '80px']}>
                {boatData && info && (
                    <Box theme={layoutTheme} mr={[0, 0, 0, 0, '20px']}>
                        <PageTitle
                            title={info.model}
                            subtitle={handleLeads(leads).boat}
                            titleTag="h1"
                            subtitleTag="h2"
                        />
                    </Box>
                )}

                <Box theme={layoutTheme} display={['none', null, null, null, 'block']} minWidth="400px">
                    {boatData && info && price && (
                        <BoatInfoHeader
                            condition={info.condition}
                            conditionCrm={info.conditionCrm ? info.conditionCrm : info.condition}
                            year={info.year}
                            length={info.length}
                            price={price.displayValue}
                            currency={price.currency}
                            vat={price.vat}
                            accomodation={info.accomodation}
                            boatID={boatData.id}
                        />
                    )}
                </Box>
            </Flex>
            <Box theme={layoutTheme} px="20px" mb={['20px', null, null, '180px']} maxWidth="1600px" mx="auto">
                {boatData && attachments && attachments.images && (
                    <BoatMainImage
                        image={getImageURL(attachments.images, { position: 'hero', size: null, hasFallback: true })}
                        mobileImage={getImageURL(attachments.images, {
                            position: 'hero',
                            size: 'featured',
                            hasFallback: true
                        })}
                        alt={info.name}
                        noAboveTheFold
                        hasGallery={exteriorImages && exteriorImages.length > 0}
                        galleryImages={exteriorImages && exteriorImages.length > 0 ? exteriorImages : null}
                    />
                )}
            </Box>

            <Flex
                theme={layoutTheme}
                flexDirection={['column', null, 'row']}
                justifyContent="space-between"
                maxWidth={1280}
                mx="auto"
                px={['20px', null, null, null, '20px']}
                mb={['80px', null, null, '140px']}>
                <Box theme={layoutTheme} width={[null, null, '48%']} mb={[0, 0, 0, '40px']} maxWidth="640px">
                    <Box theme={layoutTheme} display={['block', null, null, null, 'none']}>
                        {boatData && info && price && (
                            <BoatInfoHeader
                                condition={info.condition}
                                conditionCrm={info.conditionCrm ? info.conditionCrm : info.condition}
                                year={info.year}
                                length={info.length}
                                price={price.displayValue}
                                currency={price.currency}
                                vat={price.vat}
                            />
                        )}
                    </Box>

                    <BoatDescription title={`${t('about')}${info ? ` ${info.model}` : ''}`}>
                        {boatData && highlights ? (
                            // eslint-disable-next-line react/no-danger
                            <p dangerouslySetInnerHTML={{ __html: highlights.about[language || 'en'] }} />
                        ) : (
                            <p>NO_CRM description</p>
                        )}
                    </BoatDescription>

                    <Box theme={layoutTheme} display={['none', null, 'block']}>
                        <BoatStatement />
                    </Box>
                </Box>

                <Box theme={layoutTheme} width={[null, null, '320px', '48%']} maxWidth="505px">
                    {boatData && info && engine && (
                        <BoatSpecifications
                            condition={info.condition}
                            conditionCrm={info.conditionCrm ? info.conditionCrm : info.condition}
                            year={info.year}
                            type={info.type}
                            brand={info.brand}
                            cabins={info.cabins}
                            length={info.length}
                            beam={info.beam}
                            draft={info.draft}
                            engines={`${engine.count} × ${engine.type}`}
                            power={engine.power}
                            speedRange={engine.range || 'NO_DATA'}
                            boatID={boatData.id}
                        />
                    )}

                    <Box theme={layoutTheme} display={['block', null, 'none']}>
                        <BoatStatement />
                    </Box>
                </Box>
            </Flex>

            {exteriorImages && exteriorImages.length > 0 && (
                <>
                    <Box
                        theme={layoutTheme}
                        mb={[20, null, null, 80]}
                        px={[20, null, null, null, 60]}
                        mx="auto"
                        maxWidth={1360}>
                        <BlockTitle title={t('exterior')} subtitle={handleLeads(leads).exterior} />
                    </Box>

                    <Box theme={layoutTheme} mb={[60, null, 100, 200]}>
                        {process.browser && <PhotoGallerySlider images={exteriorImages} gtmPDPExterior />}
                    </Box>
                </>
            )}

            {interiorImages && interiorImages.length > 0 && (
                <>
                    <Box
                        theme={layoutTheme}
                        mb={[20, null, null, 80]}
                        px={[20, 'auto', 'auto', 'auto', 60]}
                        mx="auto"
                        maxWidth={1360}>
                        <BlockTitle title={t('interior')} subtitle={handleLeads(leads).interior} />
                    </Box>

                    <Box theme={layoutTheme} mb={[60, null, 100, 200]}>
                        {process.browser && <PhotoGallerySlider images={interiorImages} gtmPDPInterior />}
                    </Box>
                </>
            )}

            {boatAttachmentVideo ? (
                <Box theme={layoutTheme} mb={['100px', null, null, '200px']} px={[20, null, null, null]}>
                    <BoatVideo
                        data={boatDescriptionsVideo}
                        videoSmall={boatAttachmentVideo.urlSmall}
                        video={boatAttachmentVideo.url}
                    />
                </Box>
            ) : null}

            {matterportLink && (
                <Box theme={layoutTheme} px="20px" mb={['110px', null, null, '200px']} maxWidth="1340px" mx="auto">
                    <Matterport matterportLink={matterportLink} />
                </Box>
            )}

            <Box theme={layoutTheme} px="20px" mb={['110px', null, null, '200px']} maxWidth="1340px" mx="auto">
                <HelpBlock boatName={`${info.model} ${info.name}`} boatID={boatData.id} />
            </Box>

            {featuredYachts.length > 1 && (
                <Box theme={layoutTheme} mb={[20, null, null, 80]} px="40px" mx="auto" maxWidth={1360}>
                    <Box theme={layoutTheme} maxWidth={600}>
                        <BlockTitle title={t('other-yachts')} variation="primary" paddingLeft={0} noWordBreak />
                    </Box>
                </Box>
            )}

            {featuredYachts.length > 1 && (
                <Box theme={layoutTheme} mb={[80, null, null, 200]}>
                    {process.browser && (
                        <PhotoGallerySlider images={featuredYachts} titleVariation="secondary" featuredYachts />
                    )}
                </Box>
            )}

            <Box theme={layoutTheme} px="20px" mb={['60px', null, null, '180px']} maxWidth="1640px" mx="auto">
                <PromoBlock
                    image={`${CLOUDINARY_URL}/v1582634009/Static/promo-${randomPromo}.jpg`}
                    title={t(`promo-title-${randomPromo}`)}
                    subtitle={t(`promo-lead-${randomPromo}`)}
                    text={t(`promo-content-${randomPromo}`)}
                    buttonText={t('about-us')}
                    buttonLink="/about"
                />
            </Box>
        </div>
    )
}

export async function getServerSideProps({ query, req = {}, res }) {
    // const { url: boatUrl } = req
    const { slug } = query
    let error = null

    if (query && query['sentry-test']) {
        await query[query['sentry-test']]()
    }

    try {
        const url = getBoatApiURL(slug)
        const ssrBoatData = !process.browser
            ? await useFetch.backendService.handleResponse(await fetch(url))
            : undefined
        const boatData = ssrBoatData && !ssrBoatData.isError ? ssrBoatData.fetchedResult : undefined
        const canonical = boatData ? getBoatLink((boatData || {}).info.type, slug, 'en').as : undefined
        const boatUrl = boatData ? getBoatLink((boatData || {}).info.type, slug, req.language, true).as : undefined
        // if (boatUrl && boatUrl !== req.originalUrl) {
        //     return res.redirect(301, boatUrl)
        // }

        if (boatData && !boatData.published) {
            error = 404
        }

        const matterportLink = boatData?.matterportLink

        return {
            props: {
                error,
                slug,
                canonical,
                boatUrl,
                ssrBoatData: boatData,
                matterportLink,
                namespacesRequired: ['common', 'controls', 'meta-data']
            }
        }
    } catch (e) {
        //
    }
    return {
        props: {
            slug,
            error: 404,
            namespacesRequired: ['common', 'controls', 'meta-data']
        }
    }
}

Boat.propTypes = {
    t: PropTypes.func.isRequired,
    error: PropTypes.number,
    slug: PropTypes.string.isRequired,
    canonical: PropTypes.string,
    boatUrl: PropTypes.string,
    ssrBoatData: PropTypes.oneOfType([() => null, PropTypes.object]),
    i18n: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    matterportLink: PropTypes.string
}

Boat.defaultProps = {
    error: null,
    canonical: undefined,
    boatUrl: undefined,
    ssrBoatData: {},
    matterportLink: null
}

export default withTranslation('common', 'controls', 'meta-data')(Boat)
